import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Container, Row, Col, Button, Modal, Form, Table, Nav, Tab, InputGroup } from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { firestore as db } from "../config/Config";
import { auth } from "../config/Config";
import { useSelector, useDispatch } from "react-redux";
import { fetchProjectList, fetchAllProjectList } from "../redux/projectReducer";
import { createClients } from "../redux/createProject";
import Sidebar from "./SidebarNav";
import '../components/Projects.css';
import Swal from 'sweetalert2';
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { Triangle } from 'react-loader-spinner';
import { useForceLogout } from "../hooks/useForceLogout";
import Fuse from "fuse.js";

// ─── API base ─────────────────────────────────────────────────────────────
// Centralized so it's not repeated (and hardcoded) in every fetch call.
// Swap this for an env var (e.g. process.env.REACT_APP_API_BASE) when deploying.
const API_BASE = "https://us-central1-fabdiz-quote.cloudfunctions.net/api";

async function apiRequest(path, options = {}) {
  const response = await fetch(`${API_BASE}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  let data = null;
  try {
    data = await response.json();
  } catch {
    // no JSON body — fine for some responses
  }
  if (!response.ok) {
    throw new Error(data?.message || `Request failed (${response.status})`);
  }
  return data;
}

// ─── Date helper ────────────────────────────────────────────────────────────
// Dates from the backend are stored as "DD/MM/YYYY" strings (confirmed from
// the API routes), so `new Date(dateString)` alone misparses them as
// MM/DD/YYYY. This mirrors the same parsing logic used server-side.
function parseStoredDate(dateStr) {
  if (!dateStr) return new Date(0);
  try {
    const parsed = new Date(dateStr.split('/').reverse().join('-'));
    return isNaN(parsed.getTime()) ? new Date(0) : parsed;
  } catch {
    return new Date(0);
  }
}

function formatDisplayDate(dateVal) {
  if (!dateVal) return '-';
  if (typeof dateVal === 'string') {
    const trimmed = dateVal.trim();
    const ddmmyyyyMatch = trimmed.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (ddmmyyyyMatch) {
      const day = ddmmyyyyMatch[1].padStart(2, '0');
      const month = ddmmyyyyMatch[2].padStart(2, '0');
      const year = ddmmyyyyMatch[3];
      return `${day}/${month}/${year}`;
    }
    const d = new Date(trimmed);
    if (!isNaN(d.getTime())) {
      return d.toLocaleDateString('en-GB');
    }
    const parsed = parseStoredDate(trimmed);
    if (parsed && !isNaN(parsed.getTime()) && parsed.getTime() !== 0) {
      return parsed.toLocaleDateString('en-GB');
    }
    return trimmed;
  }
  if (dateVal instanceof Date && !isNaN(dateVal.getTime())) {
    return dateVal.toLocaleDateString('en-GB');
  }
  const d = new Date(dateVal);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB');
}

// ─── Pricing constants ────────────────────────────────────────────────────────
// CONST_BASE depends on both location and selected price type:
//   BLR  → New Price: 1970  |  Old Price: 1600
//   KKT  → New Price: 2280  |  Old Price: 2280  (same base regardless of type)
// CONST_LIMIT (floor price) is also location-aware:
//   BLR → 1980  |  KKT → 2280
const PRICE_TYPE_BASE = {
  blr: { value: 1970, oldvalue: 1600 },
  kkt: { value: 1600, oldvalue: 1600 },
};
// ─────────────────────────────────────────────────────────────────────────────

export default function Projects() {
  const userDetailsLocal = localStorage.getItem('userDetails');
  const userDetailsLo = JSON.parse(userDetailsLocal);
  console.log(userDetailsLo)

  useForceLogout();
  useEffect(() => {
    window.localStorage.setItem('maxDiscountApproved', 0);
  }, []);

  // ─── Location-based increment (from stored user profile, same as Dashboard) ──
  const userLocation = userDetailsLo?.location?.toLowerCase?.() || '';
  const isBLR = userLocation === 'blr';
  const isKKT = userLocation === 'kkt';
  const baseIncrement = isBLR ? 65 : 70;
  // ──────────────────────────────────────────────────────────────────────────────

  // ─── Role check ─────────────────────────────────────────────────────────
  // The Created By filter is only meaningful for a "head" who manages a team
  // and sees clients across multiple creators — a regular user only ever
  // sees their own projects, so the filter would always have one option.
  const isHead = userDetailsLo?.role === 'head';
  // ─────────────────────────────────────────────────────────────────────────

  const [search, setSearch] = useState('');
  const [projectName, setProjectName] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [configuration, setConfiguration] = useState("");
  const [show, setShow] = useState(false);
  const [markOn, setMarkOn] = useState(false);
  const [clientID, setClientID] = useState("");
  const [userIncrement, setUserIncrement] = useState(0);
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [validated, setValidated] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [firstID, setFirstID] = useState('');
  const [secondID, setSecondID] = useState('');
  const [incr, setIncr] = useState(0);

  const [statuesOn, setStatuesOn] = useState(false);
  const [statuesLostOn, setStatuesLostOn] = useState(false);
  const [statuesdeleteOn, setStatuesdeleteOn] = useState(false);
  const [clientModalId, setClientModalId] = useState("");
  const [clientModalLostId, setClientModalLostId] = useState("");
  const [clientModalDeleteId, setClientModalDeleteId] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [lim, setLim] = useState(10);

  const location = useLocation();
  const navigation = useNavigate();
  const dispatch = useDispatch();

  const { data, allData, loader, error, status } = useSelector((state) => state.projects);
  const { userData, userLoader, userError } = useSelector((state) => state.viewusers);
  const userUID = localStorage.getItem('userUID');

  const [selectedPriceType, setSelectedPriceType] = useState('');

  // ─── Live computed values (same logic as Dashboard) ───────────────────────
  const locationKey = isKKT ? 'kkt' : 'blr';
  const constBase = selectedPriceType ? (PRICE_TYPE_BASE[locationKey]?.[selectedPriceType] ?? null) : null;
  const constLimit = isKKT ? 2280 : 1980;
  const finalIncrement = baseIncrement + Number(userIncrement || 0);
  const totalHikedBase = constBase ? Math.round(constBase * (1 + finalIncrement / 100)) : null;
  const computedMaxDiscount = totalHikedBase
    ? Math.floor((100 - ((constLimit / totalHikedBase) * 100)) / 5) * 5
    : null;
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(fetchProjectList({ userUID, page: currentPage, lim }));
  }, [currentPage, lim, dispatch, userUID]);

  // Tracks whether the very first page load has completed. Used to show the
  // full-screen Triangle loader only on initial load — subsequent page
  // changes keep the table visible and show a small spinner in the
  // Previous/Next buttons instead, so the table doesn't flicker/vanish.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  useEffect(() => {
    if (data && !hasLoadedOnce) {
      setHasLoadedOnce(true);
    }
  }, [data, hasLoadedOnce]);

  // Fixed: previously depended on `data`, causing the full unpaginated list
  // to be re-fetched every time the paginated page changed. Only refetch when
  // the user itself changes.
  useEffect(() => {
    dispatch(fetchAllProjectList({ userUID }));
  }, [userUID, dispatch]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      const routeName = user !== null ? '/projects' : '/login';
      navigation(routeName);
    });
    return () => unsubscribe();
  }, [navigation]);

  const createProject = async () => {
    if (isCreatingProject) return;
    setIsCreatingProject(true);
    try {
      await apiRequest('/project/create', {
        method: 'POST',
        body: JSON.stringify({
          ID: userUID,
          pname: projectName.trim(),
          cname: clientName.trim(),
          cphone: clientPhone.trim() === '' ? null : Number(clientPhone.trim()),
          cemail: clientEmail.trim(),
          city: city.trim(),
          state: state.trim(),
          configuration: configuration.trim(),
          date: new Date().toLocaleDateString('en-GB'),
          time: new Date().toLocaleTimeString('en-GB'),
          status: "Ongoing",
          createdBy: userDetailsLo?.name,
          price: "",
          priceType: selectedPriceType,
          database: 'pricelist04072026',
          increment: finalIncrement,
          constBase: constBase,
          constLimit: constLimit,
          totalHikedBase: totalHikedBase,
          maxDiscount: computedMaxDiscount,
        }),
      });

      dispatch(fetchProjectList({ userUID, page: currentPage, lim }));
      dispatch(fetchAllProjectList({ userUID }));


      setShow(false);
      setProjectName('');
      setClientName('');
      setClientPhone('');
      setClientEmail('');
      setCity('');
      setState('');
      setSelectedPriceType('');
      setConfiguration('');
      setUserIncrement(0);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Failed to create project',
        text: err.message,
      });
    } finally {
      setIsCreatingProject(false);
    }
  };

  const handleComplete = () => {
    dispatch(fetchProjectList({ userUID, page: currentPage, lim }));
    setMarkOn(false);
  };

  const handleLogOut = () => { auth.signOut(); };

  const handleSubmit = (event) => {
    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    setValidated(true);
  };

  const [importPricetype, setImportPricetype] = useState('');
  const [nameimportTo, setNameimportTo] = useState('');
  const [importingFrom, setImportingFrom] = useState('');

  // Split into distinct open/close handlers — previously a single function
  // was reused for both, and calling it with no argument (from the close
  // button) tried to read `.priceType` off a click event instead of a client.
  const openImportModal = (client) => {
    setImportPricetype(client.priceType);
    setSecondID(client.id);
    setIncr(client.increment);
    setNameimportTo(client.cname);
    setImportModal(true);
  };

  const closeImportModal = () => {
    setImportModal(false);
    setFirstID('');
    setImportingFrom('');
  };

  const handleSelectRoomToImport = (option) => {
    if (!option) {
      setFirstID('');
      setImportingFrom('');
      return;
    }
    setFirstID(option.value);
    setImportingFrom(option.label);
  };

  const [loading, setLoading] = useState(false);

  // Firestore batched writes cap at 500 operations per batch.
  const FIRESTORE_BATCH_LIMIT = 500;

  const copyDocuments = async (fID, sID) => {
    if (!fID || !sID) {
      Swal.fire({ icon: 'warning', title: 'Select a project to import from first.' });
      return;
    }

    const result = await Swal.fire({
      title: "Import Confirmation",
      html: `
        <div style="font-size: 18px; color: #333;">
          <p>Are you sure you want to import from <b style="color: #d9534f;">${importingFrom}</b>
          to <b style="color: #d9534f;">${nameimportTo}</b>?</p>
        </div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Import",
      cancelButtonText: "No, Cancel"
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      Swal.fire({
        title: "Importing...",
        text: "Please wait while the documents are being copied.",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      const snapshot = await db.collection("client").doc(fID).collection("Rooms").get();
      const docs = snapshot.docs;

      if (!docs || docs.length === 0) {
        Swal.fire({
          title: "No Rooms Found",
          text: "The selected project has no room documents to import.",
          icon: "info",
          confirmButtonText: "OK"
        });
        return;
      }

      // Exclude temporary or unsaved draft documents
      const validDocs = docs.filter(docSnap => {
        const docId = docSnap.id || '';
        if (docId.startsWith('draft_')) return false;
        const d = docSnap.data();
        if (!d) return false;
        if (d.isDraft === true || d.draftTag === 'Draft Saved Copy' || d.isDraftCopy === true) return false;
        return true;
      });

      if (validDocs.length === 0) {
        Swal.fire({
          title: "No Valid Rooms Found",
          text: "The selected project has no published room designs to import.",
          icon: "info",
          confirmButtonText: "OK"
        });
        return;
      }

      // Chunk into batches of <=500 writes and commit each batch atomically.
      for (let i = 0; i < validDocs.length; i += FIRESTORE_BATCH_LIMIT) {
        const chunk = validDocs.slice(i, i + FIRESTORE_BATCH_LIMIT);
        const batch = db.batch();

        chunk.forEach((docSnap) => {
          const rawData = docSnap.data();
          // Clone room data completely while updating client/user linkage
          const data = {
            ...JSON.parse(JSON.stringify(rawData)),
            userId: sID,
            clientId: sID,
            importedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          const targetRef = db.collection("client").doc(sID).collection("Rooms").doc(docSnap.id);
          batch.set(targetRef, data);
        });

        await batch.commit();
      }

      // Refresh project lists in Redux
      dispatch(fetchProjectList({ userUID, page: currentPage, lim }));
      dispatch(fetchAllProjectList({ userUID }));

      Swal.fire({
        title: "Import Complete",
        text: "All documents have been successfully copied.",
        icon: "success",
        confirmButtonText: "OK"
      });
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "There was an issue copying the documents. Please try again.",
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setLoading(false);
      closeImportModal();
    }
  };

  // Fixed: previously read `statuesOn` (the "complete" modal's state) instead
  // of `statuesLostOn`, so the Lost modal's open/close could get out of sync
  // with the Complete modal.
  const statuesModel = (id) => { setClientModalId(id); setStatuesOn(true); };
  const statuesModelLost = (id) => { setClientModalLostId(id); setStatuesLostOn(true); };
  const statuesModelDelete = (id) => { setClientModalDeleteId(id); setStatuesdeleteOn(true); };

  const handleStatuesClose = () => setStatuesOn(false);
  const handleLostStatuesClose = () => setStatuesLostOn(false);
  const handleStatuesdeleteClose = () => setStatuesdeleteOn(false);

  const handleClose = () => setShow(false);
  const handleMarkClose = () => setStatuesOn(false);
  const handleMarkLostClose = () => setStatuesLostOn(false);

  const updateClientStatus = async (cid, newStatus) => {
    return apiRequest('/updateClientStatus', {
      method: 'PUT',
      body: JSON.stringify({ cid, newStatus }),
    });
  };

  const handleStatuesComplete = async () => {
    try {
      await updateClientStatus(clientModalId, "completed");
      dispatch(fetchProjectList({ userUID, page: currentPage, lim }));
      dispatch(fetchAllProjectList({ userUID }));
      setStatuesOn(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed to update status', text: err.message });
    }
  };

  const handleStatusLost = async () => {
    try {
      await updateClientStatus(clientModalLostId, "lost");
      dispatch(fetchProjectList({ userUID, page: currentPage, lim }));
      dispatch(fetchAllProjectList({ userUID }));
      setStatuesLostOn(false);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed to update status', text: err.message });
    }
  };

  const handleDelete = async () => {
    try {
      Swal.fire({
        title: 'Deleting...',
        text: 'Please wait while we delete the client.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      await apiRequest('/deleteClient', {
        method: 'DELETE',
        body: JSON.stringify({ cid: clientModalDeleteId }),
      });

      dispatch(fetchProjectList({ userUID, page: currentPage, lim }));
      dispatch(fetchAllProjectList({ userUID }));
      setStatuesdeleteOn(false);
      Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Client has been successfully deleted.', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed!', text: err.message || 'Something went wrong.' });
    }
  };

  const [openImportSearchBar, setOpenImportSearchBar] = useState(false);
  const [createdByFilter, setCreatedByFilter] = useState('all');

  // ─── Sorted paginated list (server-driven page) ────────────────────────────
  const sortedData = useMemo(() => {
    if (!data?.clients) return [];
    return [...data.clients].sort((a, b) => {
      const dateTimeA = a.dateTimec ? new Date(a.dateTimec) : parseStoredDate(a.date);
      const dateTimeB = b.dateTimec ? new Date(b.dateTimec) : parseStoredDate(b.date);
      return dateTimeB.getTime() - dateTimeA.getTime();
    });
  }, [data]);

  // ─── Created By filter options ─────────────────────────────────────────────
  // Derived from the full dataset so the dropdown always reflects every
  // creator that exists, not just whoever appears on the current page.
  const createdByOptions = useMemo(() => {
    const names = new Set();
    (allData?.clients || []).forEach((item) => {
      if (item.createdBy) names.add(item.createdBy);
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [allData]);

  // ─── Search + filter ────────────────────────────────────────────────────
  // Searches/filters the full (unpaginated) client list across project name,
  // client name, phone, city, and status, combined with the Created By
  // dropdown — memoized so it only recomputes when inputs actually change.
  const trimmedSearch = search.trim().toLowerCase();
  const isFilteringByCreator = createdByFilter !== 'all';
  const isSearching = trimmedSearch.length > 0;
  const isFiltering = isSearching || isFilteringByCreator;

  // ─── Fuse.js Search Instances ──────────────────────────────────────────
  // Main search box Fuse instance
  const mainFuse = useMemo(() => {
    const clients = allData?.clients || [];
    if (!clients.length) return null;
    return new Fuse(clients, {
      keys: [
        { name: "pname", weight: 0.4 },
        { name: "cname", weight: 0.3 },
        { name: "cphone", weight: 0.15 },
        { name: "city", weight: 0.1 },
        { name: "status", weight: 0.05 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1
    });
  }, [allData]);

  // Import Rooms modal Fuse instance
  const importOptions = useMemo(() => {
    return (allData?.clients || []).map((item) => ({
      label: `${item.pname || ''} (${item.cname || ''})`,
      pname: item.pname || '',
      cname: item.cname || '',
      value: item.id,
      pricetype: item.priceType
    }));
  }, [allData]);

  const importFuse = useMemo(() => {
    if (!importOptions.length) return null;
    return new Fuse(importOptions, {
      keys: [
        { name: "pname", weight: 0.5 },
        { name: "cname", weight: 0.3 },
        { name: "label", weight: 0.2 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1
    });
  }, [importOptions]);

  const filteredResults = useMemo(() => {
    if (!isFiltering) return null;
    let source = allData?.clients || [];

    if (isSearching) {
      if (mainFuse) {
        source = mainFuse.search(trimmedSearch).map((r) => r.item);
      } else {
        source = source.filter((item) => {
          const haystacks = [
            item.pname,
            item.cname,
            item.cphone != null ? String(item.cphone) : '',
            item.city,
            item.status,
          ];
          return haystacks.some((field) =>
            (field || '').toString().toLowerCase().includes(trimmedSearch)
          );
        });
      }
    }

    if (isFilteringByCreator) {
      source = source.filter((item) => item.createdBy === createdByFilter);
    }

    return [...source].sort((a, b) => {
      const dateTimeA = a.dateTimec ? new Date(a.dateTimec) : parseStoredDate(a.date);
      const dateTimeB = b.dateTimec ? new Date(b.dateTimec) : parseStoredDate(b.date);
      return dateTimeB.getTime() - dateTimeA.getTime();
    });
  }, [isFiltering, isSearching, isFilteringByCreator, createdByFilter, trimmedSearch, allData, mainFuse]);

  const rowsToDisplay = isFiltering ? (filteredResults || []) : sortedData;
  const totalUnfilteredCount = allData?.clients?.length ?? data?.total ?? 0;

  const handleSearchChange = useCallback((e) => {
    setSearch(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearch('');
  }, []);

  const handleCreatedByChange = useCallback((e) => {
    setCreatedByFilter(e.target.value);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSearch('');
    setCreatedByFilter('all');
  }, []);

  return (
    <Container fluid>
      <Row className="m-0">
        <Col sm={1}></Col>

        <Col className="" sm={11}>
          <hr />
          <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <button className="buttonDesign" onClick={() => setShow(true)}>
                Create New Project
              </button>

              {/* ── Created By filter — head role only ── */}
              {isHead && (
                <Form.Select
                  value={createdByFilter}
                  onChange={handleCreatedByChange}
                  style={{
                    borderRadius: "10px",
                    padding: "8px 12px",
                    border: "1px solid #d1d5db",
                    width: "auto",
                    minWidth: "180px",
                  }}
                >
                  <option value="all">All Created By</option>
                  {createdByOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </Form.Select>
              )}

              {isFiltering && (
                <button
                  type="button"
                  className="buttonDesign"
                  onClick={handleClearFilters}
                  style={{ backgroundColor: "#e2e8f0", color: "#4a5568" }}
                >
                  Clear filters
                </button>
              )}
            </span>

            <Form>
              <InputGroup>
                <Form.Control
                  style={{
                    borderRadius: "15px",
                    padding: "12px 15px",
                    border: "1px solid #d1d5db",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                    background: "linear-gradient(145deg, #ffffff, #f3f4f6)",
                    transition: "all 0.3s ease",
                    width: "20vw",
                  }}
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search by project, client, phone, city, or status"
                />
                {search && (
                  <Button
                    variant="outline-secondary"
                    onClick={handleClearSearch}
                    style={{ borderRadius: "0 15px 15px 0" }}
                    aria-label="Clear search"
                  >
                    ×
                  </Button>
                )}
              </InputGroup>
            </Form>
          </span>

          {isFiltering && (
            <div style={{ marginTop: "8px", fontSize: "13px", color: "#6b7280" }}>
              Showing {rowsToDisplay.length} of {totalUnfilteredCount} projects
              {isFilteringByCreator && <> — created by <strong>{createdByFilter}</strong></>}
              {isSearching && <> — matching "<strong>{search.trim()}</strong>"</>}
            </div>
          )}

          <hr />
        </Col>
      </Row>

      <Row style={{ padding: '0px 0px 0px 20px' }}>
        <Col sm={1}></Col>
        <Col sm={11}>
          <div className="table-container">
            <div className="tableScroll">
              {loader && !hasLoadedOnce ? (
                <center>
                  <Triangle
                    height="150"
                    color="#ff5100"
                    ariaLabel="triangle-loading"
                    radius="1"
                    wrapperStyle={{ flex: 1, alignItems: 'center', justifyContent: 'center', height: "60vh" }}
                    visible={true}
                  />
                </center>
              ) : data ? (
                <table>
                  <thead>
                    <tr>
                      <th>Project Name</th>
                      <th>Client Name</th>
                      <th>Phone Number</th>
                      <th>City</th>
                      <th>Date Created</th>
                      <th>Status</th>
                      <th>Created By</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className="hover-content">
                    {rowsToDisplay.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ textAlign: 'center', padding: '20px' }}>
                          {isFiltering ? 'No matching projects found.' : 'No projects yet.'}
                        </td>
                      </tr>
                    ) : (
                      rowsToDisplay.map((e) => (
                        <tr key={e.id}>
                          <td style={{ textAlign: 'start' }}>{e.pname}</td>
                          <td>{e.cname}</td>
                          <td>{e.cphone}</td>
                          <td>{e.city}</td>
                          <td>{formatDisplayDate(e.date || e.dateTimec || e.timestamp)}</td>
                          <td>{e.status}</td>
                          <td>{e.createdBy}</td>
                          <td>
                            <Button
                              variant="primary"
                              className="buttonDesign"
                              onClick={() => navigation('/projects/view-rooms', { state: e })}
                            >
                              View Rooms
                            </Button>
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant="primary">More options</Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item className="editItem" onClick={() => openImportModal(e)}>Import Rooms</Dropdown.Item>
                                <Dropdown.Item className="editItem" onClick={() => statuesModel(e.id)}>Mark as Complete</Dropdown.Item>
                                <Dropdown.Item className="editItem" onClick={() => statuesModelLost(e.id)}>Mark as Lost</Dropdown.Item>

                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              ) : null}
            </div>
          </div>
        </Col>
      </Row>

      <Row style={{ marginTop: '1vh' }}>
        <Col sm={1}></Col>
        <Col sm={11}>
          <hr />
          {!isFiltering && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
              <button
                className="buttonDesign"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1 || loader}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                {loader && <CircularProgress size={14} color="inherit" />}
                Previous
              </button>
              <span>Page {currentPage}</span>
              <button
                className="buttonDesign"
                onClick={() => setCurrentPage(prev => prev + 1)}
                disabled={!data?.hasNextPage || loader}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                {loader && <CircularProgress size={14} color="inherit" />}
                Next
              </button>
            </div>
          )}
        </Col>
      </Row>


      {/* ── Create Project Modal ─────────────────────────────────────────── */}
      <Modal show={show} onHide={handleClose} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '26px', fontWeight: '700', color: '#e75b12' }}>
            Create Project
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tables" style={{
          marginBottom: '50px',
          padding: '20px',
          marginLeft: '20px',
          borderRadius: '15px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#ffffff',
        }}>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            {[
              { label: 'Client name', type: 'text', value: clientName, setter: setClientName },
              { label: 'Client Phone', type: 'number', value: clientPhone, setter: setClientPhone },
              { label: 'Client Email', type: 'email', value: clientEmail, setter: setClientEmail },
              { label: 'Property name', type: 'text', value: projectName, setter: setProjectName },
              { label: 'City', type: 'text', value: city, setter: setCity },
              { label: 'State', type: 'text', value: state, setter: setState },
              { label: 'Configuration', type: 'text', value: configuration, setter: setConfiguration },
            ].map(({ label, type, value, setter }) => (
              <Form.Group className="mb-3" key={label}>
                <Row>
                  <Col>
                    <Form.Label style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>
                      {label}
                    </Form.Label>
                  </Col>
                  <Col>
                    <Form.Control
                      type={type}
                      value={value}
                      onChange={(e) => setter(e.target.value)}
                      required
                      style={{
                        borderRadius: '10px',
                        padding: '10px',
                        border: '1px solid #ddd',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                        backgroundColor: '#ffffff',
                      }}
                    />
                  </Col>
                </Row>
              </Form.Group>
            ))}

            {/* ── Price Type — must be selected FIRST so increment preview is accurate ── */}
            <Form.Group className="mb-3">
              <Row>
                <Col>
                  <Form.Label style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>
                    Select Price Type
                  </Form.Label>
                </Col>
                <Col>
                  <Form.Select
                    value={selectedPriceType}
                    onChange={(e) => setSelectedPriceType(e.target.value)}
                    required
                    style={{
                      borderRadius: '10px',
                      padding: '10px',
                      border: '1px solid #ddd',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    <option value="">Select</option>
                    <option value="oldvalue">Old Price</option>
                    <option value="value">New Price</option>
                  </Form.Select>
                  {constBase && (
                    <small style={{ color: '#e75b12', marginTop: '4px', display: 'block' }}>
                      Const Base = ₹{constBase}
                    </small>
                  )}
                </Col>
              </Row>
            </Form.Group>

            {/* ── Increment field — driven by logged-in user's location ── */}
            <Form.Group className="mb-3">
              <Row>
                <Col>
                  <Form.Label style={{ fontSize: '15px', fontWeight: '600', color: '#4a5568' }}>
                    Increment{' '}
                    <span style={{ fontSize: '12px', color: '#888', fontWeight: '400' }}>
                      (base {baseIncrement} + your value)
                    </span>
                  </Form.Label>

                  {isBLR && (
                    <div style={{
                      display: 'inline-block',
                      marginLeft: 8,
                      padding: '2px 10px',
                      borderRadius: 20,
                      backgroundColor: '#fff3cd',
                      color: '#856404',
                      fontSize: 11,
                      fontWeight: 600,
                    }}>
                      BLR — base 65
                    </div>
                  )}
                  {isKKT && (
                    <div style={{
                      display: 'inline-block',
                      marginLeft: 8,
                      padding: '2px 10px',
                      borderRadius: 20,
                      backgroundColor: '#e2d9f3',
                      color: '#4a235a',
                      fontSize: 11,
                      fontWeight: 600,
                    }}>
                      KKT — base 70
                    </div>
                  )}
                </Col>
                <Col>
                  <Form.Control
                    type="number"
                    min={0}
                    placeholder={`e.g. 10 → total ${baseIncrement + 10}`}
                    value={userIncrement === 0 ? '' : userIncrement}
                    onChange={(e) => setUserIncrement(e.target.value)}
                    style={{
                      borderRadius: '10px',
                      padding: '10px',
                      border: '1px solid #ddd',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                      backgroundColor: '#ffffff',
                    }}
                  />

                  {constBase ? (
                    <div style={{ marginTop: '6px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      <small style={{ color: '#e75b12', display: 'block' }}>
                        Total increment: <strong>{finalIncrement}%</strong>
                        &nbsp;→&nbsp;Hiked base: <strong>₹{totalHikedBase}</strong>
                      </small>
                      <small style={{
                        color: computedMaxDiscount < 0 ? '#e53e3e' : '#276749',
                        display: 'block',
                        fontWeight: '600',
                      }}>
                        Max discount: <strong>{computedMaxDiscount}%</strong>
                        {computedMaxDiscount < 0 && (
                          <span style={{ color: '#e53e3e', marginLeft: '6px', fontSize: '11px' }}>
                            (hiked base is below floor — increase increment)
                          </span>
                        )}
                      </small>
                    </div>
                  ) : (
                    <small style={{ color: '#aaa', marginTop: '4px', display: 'block' }}>
                      Select a price type above to see the max discount preview
                    </small>
                  )}
                </Col>
              </Row>
            </Form.Group>

          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: 'none' }}>
          <Button
            variant="secondary"
            onClick={handleClose}
            style={{
              backgroundColor: '#e2e8f0',
              color: '#4a5568',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 20px',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={createProject}
            disabled={isCreatingProject}
            style={{
              backgroundColor: '#e75b12',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 20px',
              boxShadow: '0 4px 10px rgba(90, 103, 216, 0.3)',
              color: '#ffffff',
              cursor: isCreatingProject ? 'not-allowed' : 'pointer',
              opacity: isCreatingProject ? 0.7 : 1,
            }}
          >
            {isCreatingProject ? 'Creating...' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Mark as Complete Modal */}
      <Modal show={statuesOn} onHide={handleStatuesClose}>
        <Modal.Body className="tables">
          <Modal.Title style={{ fontSize: 15, fontWeight: "bold" }}>
            Do you want to mark this project as completed?
          </Modal.Title>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleMarkClose}>Close</Button>
          <Button variant="primary" onClick={handleStatuesComplete}>Complete</Button>
        </Modal.Footer>
      </Modal>

      {/* Mark as Lost Modal */}
      <Modal show={statuesLostOn} onHide={handleLostStatuesClose}>
        <Modal.Body className="tables">
          <Modal.Title style={{ fontSize: 15, fontWeight: "bold" }}>
            Do you want to mark this project as Lost?
          </Modal.Title>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleMarkLostClose}>Close</Button>
          <Button variant="primary" onClick={handleStatusLost}>Lost</Button>
        </Modal.Footer>
      </Modal>

      {/* Mark as Delete Modal */}
      <Modal show={statuesdeleteOn} onHide={handleStatuesdeleteClose}>
        <Modal.Body className="tables">
          <Modal.Title style={{ fontSize: 15, fontWeight: "bold" }}>
            Do you want to mark this project as delete?
          </Modal.Title>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleStatuesdeleteClose}>Close</Button>
          <Button variant="primary" onClick={handleDelete}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* Import Rooms Modal */}
      <Modal show={importModal} onHide={closeImportModal} centered size="md">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "20px", fontWeight: "600", color: "#333" }}>
            Import Rooms
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: "28px 24px" }}>
          <Form noValidate validated={validated} onSubmit={handleSubmit}>
            <Form.Group className="mb-0">
              <Row className="align-items-center">
                <Col xs={12}>
                  <Autocomplete
                    disablePortal
                    options={importOptions}
                    filterOptions={(options, state) => {
                      const query = (state.inputValue || "").trim();
                      if (!query) return options;
                      if (importFuse) {
                        return importFuse.search(query).map((res) => res.item);
                      }
                      return options.filter((o) =>
                        o.label.toLowerCase().includes(query.toLowerCase())
                      );
                    }}
                    getOptionLabel={(option) => option.label || ""}
                    onChange={(event, value) => handleSelectRoomToImport(value)}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                        "& fieldset": { borderColor: "#ced4da" },
                        "&:hover fieldset": { borderColor: "#eb5e28" },
                        "&.Mui-focused fieldset": { borderColor: "#eb5e28" },
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "15px",
                        color: "#6c757d",
                        "&.Mui-focused": { color: "#eb5e28" },
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Project to Import From"
                        variant="outlined"
                        placeholder="Search by project name or client..."
                      />
                    )}
                  />
                </Col>
              </Row>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid #dee2e6", padding: "16px 24px" }}>
          <Button variant="secondary" onClick={closeImportModal}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => copyDocuments(firstID, secondID)}
            disabled={loading || !firstID}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Import"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}